import { calculateRemainingNativeQuantity } from '@einhaus/project314-shared';
import type { BrokerageOrder } from '@einhaus/project314-types';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';

export type ModifiableOrder = Pick<
    BrokerageOrder,
    'id' | 'positionId' | 'type' | 'entityType' | 'derivativeType' | 'quantity' | 'quantityFilled'
> & {
    priceExecution?: {
        targetPrice: number;
        expiresAt: number;
        status: string;
        targetSourceIdentity: string;
    } | null;
};

type ModifyOrderInput = inferRouterInputs<AppRouter>['user']['modifyOrder'];
type ModifyOrderResult = inferRouterOutputs<AppRouter>['user']['modifyOrder'];

export const modifyOrderPrice = async (
    order: ModifiableOrder,
    limitPrice: number,
    submit: (input: ModifyOrderInput) => Promise<ModifyOrderResult>
): Promise<void> => {
    const remainingQuantity = calculateRemainingNativeQuantity({
        instrument: order,
        quantity: order.quantity,
        quantityFilled: order.quantityFilled
    });

    if (!Number.isFinite(remainingQuantity) || remainingQuantity <= 0) {
        throw new Error('This order has no remaining quantity to modify. Refresh the portfolio.');
    }

    if (!Number.isFinite(limitPrice) || limitPrice <= 0) {
        throw new Error('Enter a positive limit price.');
    }

    const result = await submit({
        id: order.id,
        remainingQuantity,
        limitPrice
    });

    if (!result.success) {
        throw new Error(result.errorMessages.filter((message) => message.trim()).join('\n') || 'Failed to modify order');
    }
};
